import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { SearchColKey, searchColumnLabel } from '@/features/search/columns';
import { Popover } from '@/components/ui/Popover';
import { Check, Columns as ColumnsIcon, Grip } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

/** The search table's "Columns" control — drag to reorder, click to show/hide (mirrors the board
 *  Table's ColumnsMenu). Keeps at least one column visible so the table never goes blank. */
export default function SearchColumnsMenu({
  order,
  hidden,
  onReorder,
  onToggle,
}: {
  order: SearchColKey[];
  hidden: SearchColKey[];
  onReorder: (next: SearchColKey[]) => void;
  onToggle: (key: SearchColKey) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = (e: DragEndEvent) => {
    const from = order.findIndex((k) => k === e.active.id);
    const to = e.over ? order.findIndex((k) => k === e.over!.id) : -1;
    if (from === -1 || to === -1 || from === to) return;
    onReorder(arrayMove(order, from, to));
  };
  const visibleCount = order.filter((k) => !hidden.includes(k)).length;

  return (
    <Popover
      align="right"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          title="Show / hide & reorder columns"
          className="inline-flex min-h-[34px] items-center gap-2 rounded-lg border border-line px-2.5 text-[12px] font-medium text-fg-soft transition-colors hover:bg-layer"
        >
          <ColumnsIcon className="h-3.5 w-3.5 text-fg-muted" />
          <span>Columns</span>
        </button>
      )}
    >
      {() => (
        <div className="min-w-[210px]">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              {order.map((key) => (
                <ColumnRow key={key} colKey={key} checked={!hidden.includes(key)} locked={!hidden.includes(key) && visibleCount <= 1} onToggle={() => onToggle(key)} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </Popover>
  );
}

function ColumnRow({ colKey, checked, locked, onToggle }: { colKey: SearchColKey; checked: boolean; locked: boolean; onToggle: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: colKey });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={cn('flex items-center gap-0.5 rounded-lg', isDragging && 'relative z-10 bg-surface-raised shadow-lg shadow-black/30')}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex h-9 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded-l-lg text-fg-ghost transition-colors hover:text-fg-soft active:cursor-grabbing"
        aria-label={`Reorder ${searchColumnLabel(colKey)}`}
        title="Drag to reorder"
      >
        <Grip />
      </button>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={locked}
        onClick={onToggle}
        className={cn(
          'flex min-h-[40px] flex-1 items-center gap-2 rounded-lg px-2 text-left text-[13px] transition-colors hover:bg-layer',
          checked ? 'text-fg' : 'text-fg-soft',
          locked && 'cursor-default opacity-60 hover:bg-transparent',
        )}
      >
        <span className="flex-1">{searchColumnLabel(colKey)}</span>
        <span className="flex w-3.5 shrink-0 justify-center text-accent">{checked ? <Check /> : null}</span>
      </button>
    </div>
  );
}
